import { HHLink } from '@hinge-health/react-component-library';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid-pro';
import { routes } from '../../../constants/strings/routes';
import { ClientType, InsurerType } from '../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';

import {
  BillAmountValidationInput,
  checkBillAmountValidity,
  gridValueDisplayGenerator,
} from '../utils';
import {
  GridColumnFields,
  GridColumnHeaderNames,
} from './bill-management-types';
import { ClientCell } from './client-cell';
import { HoldTypeCell } from './hold-types-cell';
import { InsurerCell } from './insurer-cell';
import { PaymentTypeCell } from './payment-type-cell';
import { SourceCell } from './source-cell';
import { SubmissionCell } from './submission-cell';
import { SubmissionStatusCell } from './submission-status-cell';

export const getClaimIdColumn = (): GridColDef => ({
  field: GridColumnFields.CLAIM_ID,
  headerName: GridColumnHeaderNames.CLAIM_ID,
  type: 'string',
  width: 150,
  sortable: false,
  editable: false,
  resizable: false,
});

export const getHoldReasonColumn = (): GridColDef => ({
  field: GridColumnFields.HOLD_REASON,
  headerName: GridColumnHeaderNames.HOLD_REASON,
  editable: false,
  width: 150,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => (
    <HoldTypeCell
      holdType={params.row.holdDetails?.holdType}
      note={params.row.holdDetails?.note}
    />
  ),
});

export const getBillAmountColumn = (): GridColDef => ({
  field: GridColumnFields.AMOUNT,
  headerName: GridColumnHeaderNames.BILL_AMOUNT,
  width: 150,
  sortable: false,
  editable: false,
  resizable: false,
  description: 'Payment Type & Bill Amount',
  renderCell: (params: GridRenderCellParams) => (
    <PaymentTypeCell
      paymentType={params.row.paymentType}
      value={params.value}
      isBillAmountValid={checkBillAmountValidity(
        params.row as BillAmountValidationInput,
      )}
    />
  ),
});

export const getSubmissionIdColumn = (): GridColDef => ({
  field: GridColumnFields.SUBMISSION_ID,
  headerName: GridColumnHeaderNames.SUBMISSION_ID,
  width: 100,
  sortable: false,
  editable: false,
  resizable: false,
});

export const getBillIdColumn = (): GridColDef => ({
  field: GridColumnFields.BILL_ID,
  headerName: GridColumnHeaderNames.BILL_ID,
  width: 100,
  sortable: false,
  editable: false,
  resizable: false,
});

export const getUserIdColumn = (): GridColDef => ({
  field: GridColumnFields.USER_ID,
  headerName: GridColumnHeaderNames.USER_ID,
  width: 100,
  sortable: false,
  resizable: false,
  editable: false,
  renderCell: (params: GridRenderCellParams) =>
    params.value ? (
      <HHLink
        color="primary"
        href={`${routes.billing.home}/${routes.billing.userPath}/${params.value}`}
        target="_blank"
        sx={{ textDecoration: 'none' }}
      >
        {params.value}
      </HHLink>
    ) : (
      '--'
    ),
});

export const getSourceIdColumn = (): GridColDef => ({
  field: GridColumnFields.SOURCE_ID,
  headerName: GridColumnHeaderNames.SUBSCRIPTION_ID,
  width: 100,
  sortable: false,
  editable: false,
  renderCell: (params: GridRenderCellParams) => (
    <SourceCell source={params.row.source} sourceId={params.row.sourceId} />
  ),
});

export const getDateOfServiceColumn = (): GridColDef => ({
  field: GridColumnFields.DATE_OF_SERVICE,
  headerName: GridColumnHeaderNames.DATE_OF_SERVICE,
  width: 150,
  sortable: true,
  sortingOrder: ['desc', 'asc', null],
  editable: false,
});

export const getClientColumn = (clientsData: ClientType[]): GridColDef => ({
  field: GridColumnFields.CLIENT,
  headerName: GridColumnHeaderNames.CLIENT,
  width: 150,
  sortable: false,
  editable: false,
  resizable: true,
  // ACCOUNTS-AND-PLAN-MIGRATION: reads the hold's change-to clientId (bill/hold target identity), not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  renderCell: (params: GridRenderCellParams) => (
    <ClientCell
      clients={clientsData}
      holdType={params.row.holdDetails?.holdType}
      clientId={params.row.holdDetails?.clientId}
      id={params.value.id}
      name={params.value.name}
      loading={params.value.loading}
    />
  ),
});

export const getInsurerColumn = (
  insurersData: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[],
): GridColDef => ({
  field: GridColumnFields.INSURER,
  headerName: GridColumnHeaderNames.INSURER,
  width: 150,
  sortable: false,
  editable: false,
  resizable: true,
  // ACCOUNTS-AND-PLAN-MIGRATION: reads the hold's change-to insurerId (bill/hold target identity), not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  renderCell: (params: GridRenderCellParams) => (
    <InsurerCell
      insurers={insurersData}
      holdType={params.row.holdDetails?.holdType}
      insurerId={params.row.holdDetails?.insurerId}
      id={params.value.id}
      name={params.value.name}
      loading={params.value.loading}
    />
  ),
});

export const getPartnershipColumn = (): GridColDef => ({
  field: GridColumnFields.PARTNERSHIP,
  headerName: GridColumnHeaderNames.PARTNERSHIP,
  width: 150,
  sortable: false,
  editable: false,
  resizable: true,
  valueFormatter: (params): string =>
    capitalizeFirstLetterAndSpaceSnakeString(gridValueDisplayGenerator(params)),
});

export const getBillingTypeColumn = (): GridColDef => ({
  field: GridColumnFields.BILLING_TYPE,
  headerName: GridColumnHeaderNames.BILL_TYPE,
  width: 100,
  sortable: false,
  editable: false,
  valueFormatter: (params): string =>
    params === null
      ? '-'
      : capitalizeFirstLetterAndSpaceSnakeString(
          gridValueDisplayGenerator(params),
        ),
});

export const getNetsuiteIdColumn = (): GridColDef => ({
  field: GridColumnFields.NETSUITE_ID,
  headerName: GridColumnHeaderNames.NETSUITE_ID,
  width: 120,
  sortable: false,
  editable: false,
  valueFormatter: (params): string => params.value || '--',
});

export const getTransactionIdColumn = (): GridColDef => ({
  field: GridColumnFields.TRANSACTION_ID,
  headerName: GridColumnHeaderNames.TRANSACTION_ID,
  width: 150,
  sortable: false,
  editable: false,
  valueFormatter: (params): string => params.value || '--',
});

export const getSubmissionStatusColumn = (): GridColDef => ({
  field: GridColumnFields.SUBMISSION_STATUS,
  headerName: GridColumnHeaderNames.SUBMISSION_STATUS,
  width: 150,
  sortable: false,
  editable: false,
  renderCell: (params: GridRenderCellParams) => (
    <SubmissionStatusCell submissionStatus={params.value} />
  ),
});

export const getSubmissionColumn = (): GridColDef => ({
  field: GridColumnFields.SUBMISSION,
  headerName: GridColumnHeaderNames.SUBMISSION,
  width: 150,
  sortable: true,
  sortingOrder: ['desc', 'asc', null],
  editable: false,
  description: GridColumnHeaderNames.SUBMISSION,
  renderCell: (params: GridRenderCellParams) => (
    <SubmissionCell
      submissionDate={params.row.submissionDate}
      submissionAmount={params.row.submissionAmount}
    />
  ),
});

export const getMoreDetailsAction = (
  id: GridRowId,
  moreDetails: (id: GridRowId) => () => void,
): React.ReactElement => (
  <GridActionsCellItem
    key={`${id}-details`}
    label="More Details"
    icon={<ReadMoreIcon color="primary" />}
    showInMenu={false}
    onClick={moreDetails(id)}
  />
);
